import {
  createOutlet,
  deleteOutlet,
  getAllOutlets,
  updateOutlet,
  updateStationById,
} from '@/services/Outlets/outlet.services';
import {
  createStation,
  deleteStation,
  getStationByOutlet,
} from '@/services/Stations/station.service';
import { Outlet, OutletsAccessQuery, Station } from '@/types';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const outletSlice = createApi({
  reducerPath: 'outlet',
  tagTypes: ['outlet', 'stations'],
  baseQuery: fetchBaseQuery(),
  endpoints: (builder) => ({
    getOutlets: builder.query<Outlet[] | [], OutletsAccessQuery>({
      queryFn: async (query) => {
        const data = await getAllOutlets(query);
        return { data };
      },
      providesTags: ['outlet'],
    }),
    createOutlet: builder.mutation<
      Partial<Outlet>,
      { outlet: Outlet; organizationId: string; companyId: string }
    >({
      queryFn: async ({ outlet, organizationId, companyId }) => {
        const data = await createOutlet(outlet, organizationId, companyId);
        return { data };
      },
      invalidatesTags: ['outlet'],
    }),
    updateOutlet: builder.mutation<
      Partial<Outlet>,
      { outlet: Outlet; organizationId: string; companyId: string }
    >({
      queryFn: async ({ outlet, organizationId, companyId }) => {
        const data = await updateOutlet(outlet, organizationId, companyId);
        return { data };
      },
      invalidatesTags: ['outlet'],
    }),
    deleteOutlet: builder.mutation<any, { id: string; organizationId: string; companyId: string }>({
      queryFn: async ({ id, organizationId, companyId }) => {
        const data = await deleteOutlet(id, organizationId, companyId);
        return { data };
      },
      invalidatesTags: ['outlet'],
    }),
    getStations: builder.query<Station[], string | undefined>({
      queryFn: async (outletId: string | undefined) => {
        if (!outletId) return { data: [] };
        const data = await getStationByOutlet(outletId);
        return { data };
      },
      providesTags: (_result, _error, outletId) => [{ type: 'stations', id: outletId }],
    }),
    createStation: builder.mutation<Partial<Station>, Omit<Station, '_id'>>({
      queryFn: async (newStation) => {
        const data = await createStation(newStation.outletId, newStation);
        return { data };
      },
      invalidatesTags: (result: Partial<Station> | undefined) => [
        { type: 'stations', id: result?.outletId },
      ],
    }),
    updateStation: builder.mutation<
      Partial<Station>,
      { stationId: string; body: Partial<Station> }
    >({
      queryFn: async ({ stationId, body }) => {
        const data = await updateStationById(stationId, body);
        return { data };
      },
      invalidatesTags: (result: Partial<Station> | undefined) => [
        { type: 'stations', id: result?.outletId },
      ],
    }),
    deleteStation: builder.mutation({
      queryFn: async (id: string) => {
        const data = await deleteStation(id);
        return { data };
      },
      invalidatesTags: (result: Partial<Station> | undefined) => [
        { type: 'stations', id: result?.outletId },
      ],
    }),
  }),
});

export const {
  useGetOutletsQuery,
  useCreateOutletMutation,
  useUpdateOutletMutation,
  useDeleteOutletMutation,
  useGetStationsQuery,
  useLazyGetStationsQuery,
  useCreateStationMutation,
  useDeleteStationMutation,
  useUpdateStationMutation,
} = outletSlice;
